import { CalendarFilled } from '@ant-design/icons'
import { App, DatePicker, Input, message, Select } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import {
  createEmployee,
  getEmployeeCreationTemplate,
  updateEmployee,
} from '../../../../api/aopsHR.api'
import AopsCommonModalLayout from '../../../../components/apos/modal/AopsCommonModalLayout'
import '../orgChart.scss'

// 인사 정보 등록/수정 모달
const PersonFormModal = ({
  open,
  onClose,
  personalInfo: initialPersonalInfo = null,
  employmentInfo: initialEmploymentInfo = null,
  onSuccess,
  isEditMode = false,
  userId,
  employeeId,
}) => {
  const [formData, setFormData] = useState({
    personalInfo: {
      userId: '',
      name: '',
      birthDate: null,
      ssnBack: '', // 주민등록번호 뒷자리 (수정 모드용)
      contact: '',
      email: '',
      address: '',
    },
    employmentInfo: {
      joinDate: null,
      position: '',
      employeeStatus: '', // ACTIVE, ON_LEAVE, RESIGNED
      workType: '', // FULL_TIME, CONTRACT
      department: '',
      resignationDate: null,
    },
  })
  const [templateData, setTemplateData] = useState(null)
  const [loading, setLoading] = useState(false)

  // 템플릿 데이터 조회 (Select 옵션을 위해 수정 모드에서도 필요)
  useEffect(() => {
    const fetchTemplate = async () => {
      if (open) {
        try {
          setLoading(true)
          const res = await getEmployeeCreationTemplate()
          console.log('getEmployeeCreationTemplate response::', res)
          if (res.status === 200) {
            setTemplateData(res.data)
          } else {
            console.error('템플릿 조회 실패:', res.data)
            message.error('템플릿 조회에 실패했습니다.')
            onClose()
          }
        } catch (error) {
          console.error('템플릿 조회 오류:', error)
          message.error('템플릿 조회 중 오류가 발생했습니다.')
          onClose()
        } finally {
          setLoading(false)
        }
      }
    }

    fetchTemplate()
  }, [open, isEditMode, onClose])

  useEffect(() => {
    if (open) {
      if (isEditMode && initialPersonalInfo && initialEmploymentInfo) {
        // 수정 모드: 기존 데이터 로드
        // 주민등록번호 파싱 (residentRegistrationNumber 또는 ssn, ssnNumber 필드에서 가져옴)
        let ssnBack = ''
        if (initialPersonalInfo.residentRegistrationNumber) {
          // "010209-3000000" 형식에서 뒷자리 추출
          const parts =
            initialPersonalInfo.residentRegistrationNumber.split('-')
          if (parts.length === 2 && parts[1]) {
            ssnBack = parts[1].replace(/[^\d]/g, '').slice(0, 7) // 뒷 7자리
          }
        } else if (initialPersonalInfo.ssn || initialPersonalInfo.ssnNumber) {
          const fullSsn = (
            initialPersonalInfo.ssn || initialPersonalInfo.ssnNumber
          ).replace(/[^\d]/g, '')
          if (fullSsn.length >= 13) {
            ssnBack = fullSsn.slice(6, 13) // 뒷 7자리
          }
        }

        // department 매핑: teamName이나 teamGroupId를 name으로 변환
        let mappedDepartment =
          initialEmploymentInfo.teamGroupId ||
          initialEmploymentInfo.teamName ||
          ''

        if (mappedDepartment && templateData?.teamGroups) {
          // 이름(string)이 들어온 경우에만 해당 이름의 id를 찾아서 넣어줌
          if (typeof mappedDepartment === 'string') {
            const foundTeam = templateData.teamGroups.find(
              (team) => team.name === mappedDepartment,
            )
            if (foundTeam) {
              mappedDepartment = foundTeam.id // 이름 대신 ID로 세팅
            }
          }
        }

        setFormData({
          personalInfo: {
            userId: initialPersonalInfo.userId || '',
            name: initialPersonalInfo.name || '',
            birthDate: initialPersonalInfo.birthDate
              ? dayjs(initialPersonalInfo.birthDate)
              : null,
            ssnBack: ssnBack,
            contact: initialPersonalInfo.contact || '',
            email: initialPersonalInfo.email || '',
            address: initialPersonalInfo.address || '',
          },
          employmentInfo: {
            employeeStatus: initialEmploymentInfo.employeeStatus || '',
            position: initialEmploymentInfo.position || '',
            workType: initialEmploymentInfo.workType || '',
            department: mappedDepartment,
            joinDate: initialEmploymentInfo.joinDate
              ? dayjs(initialEmploymentInfo.joinDate)
              : null,
            resignationDate: initialEmploymentInfo.resignationDate
              ? dayjs(initialEmploymentInfo.resignationDate)
              : null,
          },
        })
      } else if (!isEditMode && templateData) {
        // 등록 모드: 템플릿 데이터로 초기화
        setFormData({
          personalInfo: {
            userId: '',
            name: '',
            birthDate: null,
            ssnBack: '',
            contact: '',
            email: '',
            address: '',
          },
          employmentInfo: {
            employeeStatus: templateData.employeeStatuses?.[0]?.status || '',
            position: '',
            workType: templateData.workTypes?.[0]?.workType || '',
            department: '',
            joinDate: null,
            resignationDate: null,
          },
        })
      }
    }
  }, [
    open,
    isEditMode,
    initialPersonalInfo,
    initialEmploymentInfo,
    templateData,
  ])

  const isFormValid = () => {
    const { personalInfo, employmentInfo } = formData

    // 연락처 유효성 검사
    const isContactValid = isValidPhoneNumber(personalInfo.contact)

    // 수정 모드와 등록 모드의 필수 필드가 다름
    if (isEditMode) {
      const isValid =
        personalInfo.name?.trim() &&
        personalInfo.birthDate &&
        dayjs.isDayjs(personalInfo.birthDate) &&
        isContactValid &&
        personalInfo.email?.trim() &&
        personalInfo.address?.trim() &&
        employmentInfo.position?.trim() &&
        employmentInfo.workType &&
        employmentInfo.employeeStatus &&
        employmentInfo.department &&
        employmentInfo.joinDate &&
        dayjs.isDayjs(employmentInfo.joinDate)

      // 디버깅용 로그
      // if (!isValid) {
      // console.log('Form validation failed:', {
      //   name: personalInfo.name?.trim(),
      //   birthDate: personalInfo.birthDate,
      //   isBirthDateValid: dayjs.isDayjs(personalInfo.birthDate),
      //   isContactValid,
      //   email: personalInfo.email?.trim(),
      //   address: personalInfo.address?.trim(),
      //   position: employmentInfo.position?.trim(),
      //   workType: employmentInfo.workType,
      //   employeeStatus: employmentInfo.employeeStatus,
      //   teamGroupId: employmentInfo.teamGroupId,
      //   joinDate: employmentInfo.joinDate,
      //   isJoinDateValid: dayjs.isDayjs(employmentInfo.joinDate),
      // })
      // }

      return isValid
    } else {
      const isValid =
        personalInfo.name?.trim() &&
        personalInfo.birthDate &&
        dayjs.isDayjs(personalInfo.birthDate) &&
        isContactValid &&
        personalInfo.email?.trim() &&
        personalInfo.address?.trim() &&
        employmentInfo.position?.trim() &&
        employmentInfo.workType &&
        employmentInfo.employeeStatus &&
        employmentInfo.department &&
        employmentInfo.joinDate &&
        dayjs.isDayjs(employmentInfo.joinDate)

      // 디버깅용 로그
      // if (!isValid) {
      //   console.log('Form validation failed:', {
      //     userId: personalInfo.userId,
      //     birthDate: personalInfo.birthDate,
      //     isBirthDateValid: dayjs.isDayjs(personalInfo.birthDate),
      //     isContactValid,
      //     email: personalInfo.email?.trim(),
      //     address: personalInfo.address?.trim(),
      //     position: employmentInfo.position?.trim(),
      //     workType: employmentInfo.workType,
      //     employeeStatus: employmentInfo.employeeStatus,
      //     teamGroupId: employmentInfo.teamGroupId,
      //     joinDate: employmentInfo.joinDate,
      //     isJoinDateValid: dayjs.isDayjs(employmentInfo.joinDate),
      //   })
      // }

      return isValid
    }
  }

  useEffect(() => {
    console.log('formData::', formData)
  }, [formData])

  const handleSave = async () => {
    if (loading) {
      message.warning('처리 중입니다. 잠시만 기다려주세요.')
      return
    }

    const selectedTeam = templateData?.teamGroups?.find(
      (t) => t.id === formData.employmentInfo.department,
    )
    console.log('selectedTeam::', selectedTeam)

    if (isEditMode) {
      // 수정 모드: updateEmployeeInfo API 호출
      if (!employeeId) {
        message.error('직원 ID가 없습니다.')
        return
      }

      try {
        setLoading(true)
        // 주민등록번호 조합 (앞자리 YYMMDD + 하이픈 + 뒷자리 7자리)
        const ssnFront =
          formData.personalInfo.birthDate &&
          dayjs.isDayjs(formData.personalInfo.birthDate)
            ? formData.personalInfo.birthDate.format('YYMMDD')
            : ''
        const ssnBack = formData.personalInfo.ssnBack || ''
        const residentRegistrationNumber =
          ssnFront && ssnBack ? `${ssnFront}-${ssnBack}` : null

        const param = {
          name: formData.personalInfo.name,
          birthDate: formData.personalInfo.birthDate
            ? formData.personalInfo.birthDate.format('YYYY-MM-DD')
            : null,
          residentRegistrationNumber,
          contact: formData.personalInfo.contact,
          email: formData.personalInfo.email,
          address: formData.personalInfo.address,
          joinDate: formData.employmentInfo.joinDate
            ? formData.employmentInfo.joinDate.format('YYYY-MM-DD')
            : null,
          resignationDate: formData.employmentInfo.resignationDate
            ? formData.employmentInfo.resignationDate.format('YYYY-MM-DD')
            : null,
          workType: formData.employmentInfo.workType,
          position: formData.employmentInfo.position,
          employeeStatus: formData.employmentInfo.employeeStatus,
          department: selectedTeam?.name || '',
          teamId: selectedTeam?.id || '',
        }
        console.log('updateEmployeeInfo request::', param)

        const res = await updateEmployee(employeeId, param, { userId })
        if (res.status === 200) {
          console.log('updateEmployeeInfo response::', res)
          message.success('인사정보가 수정되었습니다.')
          onSuccess?.()
          onClose()
        } else {
          message.error('인사정보 수정에 실패했습니다.')
        }
      } catch (error) {
        console.error('인사정보 수정 오류:', error)
        message.error('인사정보 수정 중 오류가 발생했습니다.')
      } finally {
        setLoading(false)
      }
    } else {
      // 등록 모드: API 호출
      try {
        setLoading(true)
        const currentName = formData.personalInfo.name
        const selectedTeam = templateData?.teamGroups?.find(
          (t) => t.id === formData.employmentInfo.department,
        )

        // 주민등록번호 조합 (앞자리 YYMMDD + 하이픈 + 뒷자리 7자리)
        const ssnFront =
          formData.personalInfo.birthDate &&
          dayjs.isDayjs(formData.personalInfo.birthDate)
            ? formData.personalInfo.birthDate.format('YYMMDD')
            : ''
        const ssnBack = formData.personalInfo.ssnBack || ''
        const residentRegistrationNumber =
          ssnFront && ssnBack ? `${ssnFront}-${ssnBack}` : null

        const param = {
          // userId: formData.personalInfo.userId,
          name: currentName,
          birthDate: formData.personalInfo.birthDate
            ? formData.personalInfo.birthDate.format('YYYY-MM-DD')
            : null,
          residentRegistrationNumber,
          contact: formData.personalInfo.contact,
          email: formData.personalInfo.email,
          address: formData.personalInfo.address,
          joinDate: formData.employmentInfo.joinDate
            ? formData.employmentInfo.joinDate.format('YYYY-MM-DD')
            : null,
          workType: formData.employmentInfo.workType,
          position: formData.employmentInfo.position,
          employeeStatus: formData.employmentInfo.employeeStatus,
          department: selectedTeam?.name || '',
          teamId: selectedTeam?.id || '',
        }
        // console.log('request::', param)

        const res = await createEmployee(param, { userId })
        if (res.status === 200) {
          message.success('인사정보가 등록되었습니다.')
          onSuccess?.()
        } else {
          message.error('인사정보 등록에 실패했습니다.')
        }
      } catch (error) {
        console.error('인사정보 등록 오류:', error)
        message.error('인사정보 등록 중 오류가 발생했습니다.')
      } finally {
        setLoading(false)
      }
    }
  }

  const formatPhoneNumber = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/[^\d]/g, '')

    // 최대 11자리로 제한 (010-xxxx-xxxx)
    const limitedNumbers = numbers.slice(0, 11)

    // 포맷팅: 010-xxxx-xxxx
    if (limitedNumbers.length <= 3) {
      return limitedNumbers
    } else if (limitedNumbers.length <= 7) {
      return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3)}`
    } else {
      return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3, 7)}-${limitedNumbers.slice(7)}`
    }
  }

  // 연락처 유효성 검사
  const isValidPhoneNumber = (phoneNumber) => {
    if (!phoneNumber || !phoneNumber.trim()) {
      return false
    }

    // 숫자만 추출
    const numbers = phoneNumber.replace(/[^\d]/g, '')

    // 한국 전화번호 형식 검증
    // 휴대폰: 010-xxxx-xxxx (11자리)
    // 일반 전화: 02-xxxx-xxxx (9-10자리), 0xx-xxx-xxxx (10-11자리)
    if (numbers.length < 9 || numbers.length > 11) {
      return false
    }

    // 0으로 시작해야 함
    if (!numbers.startsWith('0')) {
      return false
    }

    // 휴대폰 번호 (010으로 시작, 11자리)
    if (numbers.startsWith('010')) {
      return numbers.length === 11
    }

    // 일반 전화번호 (02, 031, 032 등으로 시작)
    // 02로 시작: 9-10자리
    if (numbers.startsWith('02')) {
      return numbers.length === 9 || numbers.length === 10
    }

    // 기타 지역번호 (031, 032, 041 등): 10-11자리
    return numbers.length === 10 || numbers.length === 11
  }

  const formatDateInput = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/[^\d]/g, '')

    // 최대 8자리로 제한 (YYYYMMDD)
    const limitedNumbers = numbers.slice(0, 8)

    // 8자리가 되면 자동으로 포맷팅: YYYY-MM-DD
    if (limitedNumbers.length === 8) {
      const year = limitedNumbers.slice(0, 4)
      const month = limitedNumbers.slice(4, 6)
      const day = limitedNumbers.slice(6, 8)
      return `${year}-${month}-${day}`
    }

    // 8자리 미만이면 그대로 반환 (입력 중)
    return limitedNumbers
  }

  const updatePersonalField = (field, value) => {
    let processedValue = value

    if (field === 'contact') {
      processedValue = formatPhoneNumber(value)
    }

    setFormData((prev) => {
      // 1. 먼저 새로운 개인 정보를 복사
      const updatedPersonalInfo = {
        ...prev.personalInfo,
        [field]: processedValue,
      }

      // 2. 만약 userId가 바뀐 경우에만 name을 연동 (필요 시)
      if (field === 'userId' && templateData?.users) {
        const selectedUser = templateData.users.find(
          (user) => user.id === value,
        )
        if (selectedUser) {
          updatedPersonalInfo.name = selectedUser.name
        }
      }

      return {
        ...prev,
        personalInfo: updatedPersonalInfo,
      }
    })
  }

  const updateEmploymentField = (field, value) => {
    setFormData({
      ...formData,
      employmentInfo: {
        ...formData.employmentInfo,
        [field]: value,
      },
    })
  }

  const statusOptions =
    templateData?.employeeStatuses?.map((status) => ({
      label: status.name,
      value: status.status,
    })) || []

  const departmentOptions =
    templateData?.teamGroups?.map((team) => ({
      label: team.name,
      value: team.id,
    })) || []

  const employmentTypeOptions =
    templateData?.workTypes?.map((workType) => ({
      label: workType.name,
      value: workType.workType,
    })) || []

  const getTitle = () => {
    if (isEditMode && initialPersonalInfo) {
      return (
        <>
          인사정보 수정
          <span
            style={{
              width: '1px',
              height: '16px',
              backgroundColor: '#C7CFD7',
              margin: '0 8px',
              display: 'inline-block',
            }}
          />
          <span className="person-detail-title-name">
            {initialPersonalInfo.name}
          </span>
        </>
      )
    }
    return '인사 정보 등록하기'
  }

  // 주민등록번호 뒷자리 마스킹 함수
  const maskSsnBack = (ssnBack) => {
    if (!ssnBack || ssnBack.length === 0) return ''
    if (ssnBack.length === 1) return ssnBack
    return ssnBack[0] + '*'.repeat(ssnBack.length - 1)
  }

  // 개인 기본 정보 필드 설정
  const personalInfoFields = [
    {
      key: 'name',
      label: '이름',
      type: 'input',
      placeholder: '이름을 입력해주세요',
      gridColumn: null,
    },
    {
      key: 'ssn',
      label: '주민등록번호',
      type: 'ssn',
      gridColumn: null,
    },
    {
      key: 'contact',
      label: '연락처',
      type: 'input',
      placeholder: '연락처를 입력해주세요',
      maxLength: 13,
      gridColumn: null,
    },
    {
      key: 'email',
      label: '이메일',
      type: 'input',
      placeholder: '이메일 입력해주세요',
      gridColumn: null,
    },
    {
      key: 'address',
      label: '자택 주소',
      type: 'input',
      placeholder: '주소를 입력해주세요',
      gridColumn: '1 / -1',
    },
  ]

  // 재직 정보 필드 설정
  const employmentInfoFields = [
    {
      key: 'employeeStatus',
      label: '재직 상태',
      type: 'select',
      placeholder: '재직 상태를 선택해주세요',
      options: statusOptions,
      gridColumn: null,
    },
    {
      key: 'department',
      label: '부서',
      type: 'select',
      placeholder: '부서를 선택해주세요',
      options: departmentOptions,
      gridColumn: null,
    },
    {
      key: 'position',
      label: '직무',
      type: 'input',
      placeholder: '직무를 입력해주세요',
      gridColumn: null,
    },
    {
      key: 'workType',
      label: '근무형태',
      type: 'select',
      placeholder: '근무형태를 선택해주세요',
      options: employmentTypeOptions,
      gridColumn: null,
    },
    {
      key: 'joinDate',
      label: '입사일',
      type: 'date',
      placeholder: 'YYYY-MM-DD 형식으로 입력하거나 선택해주세요',
      gridColumn: null,
    },
    {
      key: 'resignationDate',
      label: '퇴사일',
      type: 'date',
      placeholder: 'YYYY-MM-DD 형식으로 입력하거나 선택해주세요',
      gridColumn: null,
    },
  ]

  // 필드 렌더링 함수
  const renderField = (field, section) => {
    const isPersonalInfo = section === 'personalInfo'
    // 등록 모드에서 name 필드는 userId 값을 사용
    let rawValue
    rawValue = isPersonalInfo
      ? formData.personalInfo[field.key]
      : formData.employmentInfo[field.key]
    const onChange = isPersonalInfo
      ? updatePersonalField
      : updateEmploymentField

    // Select의 경우 빈 문자열을 undefined로 변환하여 placeholder가 표시되도록 함
    const value =
      field.type === 'select' && (rawValue === '' || rawValue === null)
        ? undefined
        : rawValue

    const commonProps = {
      value,
      className: field.type === 'select' ? 'form-select' : 'form-input',
      placeholder: field.placeholder,
    }

    switch (field.type) {
      case 'input':
        return (
          <Input
            {...commonProps}
            onChange={(e) => onChange(field.key, e.target.value)}
            maxLength={field.maxLength}
          />
        )
      case 'select':
        return (
          <Select
            {...commonProps}
            onChange={(value) => onChange(field.key, value)}
            options={field.options}
            style={{ width: '100%' }}
          />
        )
      case 'ssnFront': {
        // 주민등록번호 앞자리 (YYMMDD)
        const ssnFrontValue =
          rawValue && dayjs.isDayjs(rawValue)
            ? rawValue.format('YYMMDD')
            : rawValue || ''
        return (
          <Input
            {...commonProps}
            value={ssnFrontValue}
            onChange={(e) => {
              const numbersOnly = e.target.value
                .replace(/[^\d]/g, '')
                .slice(0, 6)
              if (numbersOnly.length === 6) {
                const yy = numbersOnly.slice(0, 2)
                const mm = numbersOnly.slice(2, 4)
                const dd = numbersOnly.slice(4, 6)
                const currentYearYY = Number(dayjs().format('YY'))
                const fullYearPrefix = Number(yy) > currentYearYY ? '19' : '20'
                const formatted = `${fullYearPrefix}${yy}-${mm}-${dd}`
                const parsedDate = dayjs(formatted, 'YYYY-MM-DD', true)
                if (parsedDate.isValid()) {
                  onChange(field.key, parsedDate)
                } else {
                  onChange(field.key, numbersOnly)
                }
              } else {
                onChange(field.key, numbersOnly)
              }
            }}
            maxLength={6}
            placeholder="YYMMDD"
          />
        )
      }
      case 'ssnBack': {
        // 주민등록번호 뒷자리 (첫 번째 자리만 보이고 나머지는 마스킹)
        const displayValue = maskSsnBack(rawValue || '')
        return (
          <Input
            {...commonProps}
            value={displayValue}
            onChange={(e) => {
              const inputValue = e.target.value
              // 마스킹 문자 제거하고 숫자만 추출
              const numbersOnly = inputValue.replace(/[^\d]/g, '').slice(0, 7)
              onChange(field.key, numbersOnly)
            }}
            maxLength={7}
            placeholder="주민등록번호 뒷자리"
            style={{ fontFamily: 'monospace', letterSpacing: '2px' }}
          />
        )
      }
      case 'ssn': {
        // 주민등록번호 (앞자리 + 뒷자리)
        const birthDateValue = formData.personalInfo.birthDate
        const ssnFrontValue =
          birthDateValue && dayjs.isDayjs(birthDateValue)
            ? birthDateValue.format('YYMMDD')
            : birthDateValue || ''
        const ssnBackValue = formData.personalInfo.ssnBack || ''

        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Input
              className="form-input"
              value={ssnFrontValue}
              onChange={(e) => {
                const numbersOnly = e.target.value
                  .replace(/[^\d]/g, '')
                  .slice(0, 6)
                if (numbersOnly.length === 6) {
                  const yy = numbersOnly.slice(0, 2)
                  const mm = numbersOnly.slice(2, 4)
                  const dd = numbersOnly.slice(4, 6)
                  const currentYearYY = Number(dayjs().format('YY'))
                  const fullYearPrefix =
                    Number(yy) > currentYearYY ? '19' : '20'
                  const formatted = `${fullYearPrefix}${yy}-${mm}-${dd}`
                  const parsedDate = dayjs(formatted, 'YYYY-MM-DD', true)
                  if (parsedDate.isValid()) {
                    updatePersonalField('birthDate', parsedDate)
                  } else {
                    updatePersonalField('birthDate', numbersOnly)
                  }
                } else {
                  updatePersonalField('birthDate', numbersOnly)
                }
              }}
              maxLength={6}
              placeholder="YYMMDD"
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '16px', color: '#666' }}>-</span>
            <Input
              className="form-input"
              value={ssnBackValue}
              onChange={(e) => {
                const inputValue = e.target.value
                const numbersOnly = inputValue.replace(/[^\d]/g, '').slice(0, 7)
                updatePersonalField('ssnBack', numbersOnly)
              }}
              maxLength={7}
              placeholder="주민등록번호 뒷자리"
              style={{
                flex: 1,
                fontFamily: 'monospace',
                letterSpacing: '2px',
              }}
            />
          </div>
        )
      }
      case 'date': {
        const handleDateFormat = (inputValue) => {
          const numbersOnly = inputValue?.replace(/[^\d]/g, '')
          if (
            numbersOnly &&
            numbersOnly.length === 8 &&
            !inputValue.includes('-')
          ) {
            const formatted = formatDateInput(numbersOnly)
            const parsedDate = dayjs(formatted, 'YYYY-MM-DD', true)
            if (parsedDate.isValid()) {
              onChange(field.key, parsedDate)
              return true
            }
          }
          return false
        }

        return (
          <DatePicker
            {...commonProps}
            style={{ width: '100%', height: '48px' }}
            suffixIcon={<CalendarFilled />}
            onChange={(date, dateString) => {
              // dateString이 숫자만으로 이루어진 경우 (yyyymmdd 형식)
              if (!handleDateFormat(dateString)) {
                onChange(field.key, date)
              }
            }}
            onBlur={(e) => {
              // 포커스가 벗어날 때 숫자만 입력된 경우 포맷팅
              handleDateFormat(e.target.value)
            }}
            onKeyDown={(e) => {
              // 엔터 키 입력 시 숫자만 입력된 경우 포맷팅
              if (e.key === 'Enter') {
                const inputValue = e.target.value
                if (handleDateFormat(inputValue)) {
                  e.preventDefault()
                }
              }
            }}
            format="YYYY-MM-DD"
            inputReadOnly={false}
          />
        )
      }
      default:
        return null
    }
  }

  // 템플릿 데이터가 없으면 렌더링하지 않음
  if (open && !templateData && !isEditMode) {
    return null
  }

  return (
    <App>
      <AopsCommonModalLayout open={open} onClose={onClose} title={getTitle()}>
        <div className="org-chart-modal-form">
          <div className="form-wrapper">
            {/* 개인 기본 정보 */}
            <div className="person-edit-section">
              <h3 className="person-edit-section-title">개인 기본 정보</h3>
              <div className="person-edit-grid">
                {personalInfoFields.map((field) => (
                  <div
                    key={field.key}
                    className="form-field-row"
                    style={
                      field.gridColumn ? { gridColumn: field.gridColumn } : {}
                    }
                  >
                    <label className="form-label">{field.label}</label>
                    <div className="form-input-wrapper">
                      {renderField(field, 'personalInfo')}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 재직 정보 */}
            <div className="person-edit-section">
              <h3 className="person-edit-section-title">재직 정보</h3>
              <div className="person-edit-grid">
                {employmentInfoFields.map((field) => (
                  <div
                    key={field.key}
                    className="form-field-row"
                    style={
                      field.gridColumn ? { gridColumn: field.gridColumn } : {}
                    }
                  >
                    <label className="form-label">{field.label}</label>
                    <div className="form-input-wrapper">
                      {renderField(field, 'employmentInfo')}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 하단 버튼 */}
            <div className="popup-footer">
              <button className="btn-cancel" onClick={onClose}>
                취소
              </button>
              <button
                className="btn-submit"
                onClick={handleSave}
                disabled={!isFormValid() || loading}
              >
                {isEditMode ? '수정 완료' : '인사정보 등록'}
              </button>
            </div>
          </div>
        </div>
      </AopsCommonModalLayout>
    </App>
  )
}

export default PersonFormModal
